import type { FC } from 'react';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import Markdown from 'markdown-to-jsx';
import { defineMessages, useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import Stack from '@atlaskit/primitives/stack';
import { xcss } from '@atlaskit/primitives/xcss';
import { FlagsProvider } from '@atlaskit/flag/flag-provider';
import Heading from '@atlaskit/heading/heading';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics/use-ai-events-instrumentation';
import { AISkeletonLoading } from '@atlassian/ai-components/ai-skeleton-loading';
import { AIBorder } from '@atlassian/ai-components/ai-border';
import { AISummaryLoading } from '@atlassian/ai-components/ai-summary-loading';
import { usePopupDwellTime } from '@atlassian/popup-dwell-time';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { COMMENTS_SUMMARY_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { CommentCountsContext } from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import { AISummaryFooter } from '@confluence/quick-summary/entry-points/AISummaryFooter.ts';
import {
	type SummaryAnalyticsPropertiesType,
	CommentsSummaryAnalyticsType,
} from '@confluence/quick-summary/entry-points/summarySharedTypes.ts';
import { FeedbackCollectorPropertiesProvider } from '@confluence/quick-summary/entry-points/FeedbackCollectorPropertiesProvider.ts';
import { SummaryPopupScrollableBody } from '@confluence/quick-summary/entry-points/SummaryPopupScrollableBody.ts';
import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n.ts';

import { useCombinedCommentsSummariesQueries } from './hooks/useCombinedCommentsSummariesQueries.tsx';
import { FOOTER_COMMENTS_SUMMARY_FEEDBACK_ENTRYPOINT_ID } from './feedbackEntrypointIds.tsx';
import { SummaryType } from './__types__/CommentsSummaryQuery.ts';
import { COMMENTS_SUMMARY_INTERACTION_METRIC } from './perf.config.ts';
import { CommentsSummariesError } from './CommentsSummariesErrors.tsx';

const styles = xcss({ height: '100%', font: 'font.body.large' });

const FooterObjectSidebarStyles = xcss({
	// Object sidebar padding is 16px, this ensures that the footer isn't cutoff by parent padding
	width: 'calc(100% + 32px)',
	marginLeft: 'space.negative.200',
});

const i18nBase = defineMessages({
	pageCommentsHeader: {
		id: 'ai-comments-summary.combined-comments-summary.page-comments-header',
		defaultMessage: 'Page comments',
		description:
			'Section header content for page comments summary inside the comments summary component',
	},
	inlineCommentsHeader: {
		id: 'ai-comments-summary.combined-comments-summary.inline-comments-header',
		defaultMessage: 'Inline comments',
		description:
			'Section header content for inline comments summary inside the comments summary component',
	},
});

const i18nLiveDocs = defineMessages({
	pageCommentsHeader: {
		id: 'ai-comments-summary.combined-comments-summary.page-comments-header.livedocs',
		defaultMessage: 'General comments',
		description:
			'Updated version of ai-comments-summary.combined-comments-summary.page-comments-header',
	},
});

const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

export enum CommentsSummarySource {
	Footer = 'footer',
	Header = 'header',
	Feed = 'feed',
}

type CommentsSummaryContentProps = {
	contentId: string;
	contentType: string;
	spaceKey: string;
	onRefresh?: () => void;
	shouldRefetchCommentsSummary?: boolean;
	source: CommentsSummarySource;
	isInObjectSidebar?: boolean;
	showAIDisclaimer?: boolean;
};

export const CommentsSummaryContent: FC<CommentsSummaryContentProps> = ({
	contentId,
	contentType,
	spaceKey,
	onRefresh,
	shouldRefetchCommentsSummary = false,
	source,
	isInObjectSidebar,
	showAIDisclaimer = true,
}) => {
	const intl = useIntl();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { edition } = useSessionData();
	const { footerCommentsCount, unresolvedInlineCommentsCount } = useContext(CommentCountsContext);

	const { trackAIInteractionInit, trackAIResultView, trackAIInteractionDismiss } =
		useAIEventsInstrumentation();

	const containerRef = useRef<HTMLDivElement>(null);
	const { getDwellTime } = usePopupDwellTime(containerRef);

	const contentTypeEnum = contentType === 'page' ? SummaryType.PAGE : SummaryType.BLOGPOST;

	const { data, errors, loading, refetch } = useCombinedCommentsSummariesQueries({
		contentId,
		contentType: contentTypeEnum,
		shouldSkipFooterCommentsQuery: footerCommentsCount === 0,
		shouldSkipInlineCommentsQuery: unresolvedInlineCommentsCount === 0,
	});

	const refreshHandler = () => {
		if (!loading) {
			refetch();
			onRefresh?.();
		}
	};

	const startExperienceTracking = useCallback(() => {
		experienceTracker.start({
			name: COMMENTS_SUMMARY_EXPERIENCE,
			attributes: {
				contentId,
				source,
			},
		});

		COMMENTS_SUMMARY_INTERACTION_METRIC.start();
	}, [contentId, experienceTracker, source]);

	useEffect(() => {
		if (loading) {
			startExperienceTracking();
		}
		if (!loading) {
			COMMENTS_SUMMARY_INTERACTION_METRIC.stop();
		}
	}, [loading, startExperienceTracking]);

	const footerCommentsSummary = data.footerCommentsSummaryData?.getCommentsSummary?.summary ?? '';
	const inlineCommentsSummary = data.inlineCommentsSummaryData?.getCommentsSummary?.summary ?? '';

	const loadingRef = useRef(loading);
	useEffect(() => {
		loadingRef.current = loading;
	}, [loading]);

	useEffect(() => {
		trackAIInteractionInit();

		if (shouldRefetchCommentsSummary) {
			refetch();
			onRefresh?.();
		}
		return () => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'dwelled',
					actionSubject: 'commentsSummary',
					source: 'commentsSummary',
					actionSubjectId: 'commentsSummaryDialog',
					attributes: {
						contentId,
						contentType,
						dwellTime: getDwellTime(),
					},
				},
			}).fire();
			if (loadingRef.current) {
				trackAIInteractionDismiss();
			}
		};
		// Only check auto refetch on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const summaryLocale = useMemo(() => {
		// Ok to just choose first of whichever is available from data team
		return (
			data.footerCommentsSummaryData?.getCommentsSummary?.language ||
			data.inlineCommentsSummaryData?.getCommentsSummary?.language
		);
	}, [data.footerCommentsSummaryData, data.inlineCommentsSummaryData]);

	const commentsType = useMemo(() => {
		const commentsTypeCollection: Array<CommentsSummaryAnalyticsType> = [];
		if (data.footerCommentsSummaryData)
			commentsTypeCollection.push(CommentsSummaryAnalyticsType.FOOTER);
		if (data.inlineCommentsSummaryData)
			commentsTypeCollection.push(CommentsSummaryAnalyticsType.INLINE);
		return commentsTypeCollection;
	}, [data.footerCommentsSummaryData, data.inlineCommentsSummaryData]);

	const feedbackContext = {
		commentsType,
		source,
	};

	const analyticsProperties: SummaryAnalyticsPropertiesType = {
		source: 'commentsSummary',
		additionalAnalyticsAttributes: {
			commentsType,
		},
	};

	useEffect(() => {
		if (!loading) {
			if (!errors.footerCommentsSummaryError || !errors.inlineCommentsSummaryError) {
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						name: 'commentsSummaryContainer',
						attributes: {
							contentId,
							edition,
							commentsType,
							summaryLocale,
							numPageCommentsSummarized: footerCommentsCount,
							numInlineCommentsSummarized: unresolvedInlineCommentsCount,
						},
					},
				}).fire();

				trackAIResultView();
			}
		}
	}, [
		createAnalyticsEvent,
		contentId,
		edition,
		loading,
		commentsType,
		summaryLocale,
		errors.footerCommentsSummaryError,
		errors.inlineCommentsSummaryError,
		footerCommentsCount,
		trackAIResultView,
		unresolvedInlineCommentsCount,
	]);

	const fullTextToCopy = () => {
		return `${
			footerCommentsSummary &&
			`${intl.formatMessage(i18n.pageCommentsHeader)}\n${footerCommentsSummary}`
		}\n\n${
			inlineCommentsSummary &&
			`${intl.formatMessage(i18n.inlineCommentsHeader)}\n${inlineCommentsSummary}`
		}`;
	};

	const renderContentBody = () => {
		return (
			<Stack space="space.200">
				{inlineCommentsSummary && (
					<Stack space="space.100">
						<Heading size="small">{intl.formatMessage(i18n.inlineCommentsHeader)}</Heading>
						<Markdown options={{ disableParsingRawHTML: true }}>{inlineCommentsSummary}</Markdown>
					</Stack>
				)}
				{footerCommentsSummary && (
					<Stack space="space.100">
						<Heading size="small">{intl.formatMessage(i18n.pageCommentsHeader)}</Heading>
						<Markdown options={{ disableParsingRawHTML: true }}>{footerCommentsSummary}</Markdown>
					</Stack>
				)}
				{(!errors.footerCommentsSummaryError || !errors.inlineCommentsSummaryError) && (
					<ExperienceSuccess
						name={COMMENTS_SUMMARY_EXPERIENCE}
						attributes={{ contentId, source }}
					/>
				)}
				{(errors.footerCommentsSummaryError || errors.inlineCommentsSummaryError) && (
					<CommentsSummariesError
						contentId={contentId}
						footerCommentsError={errors.footerCommentsSummaryError}
						inlineCommentsError={errors.inlineCommentsSummaryError}
						source={source}
					/>
				)}
			</Stack>
		);
	};

	const renderContent = () => {
		if (loading) {
			return isInObjectSidebar ? (
				<Box paddingInline="space.100">
					<AISummaryLoading />
				</Box>
			) : (
				<AISkeletonLoading />
			);
		}

		const shouldShowFooterError =
			(!!errors.footerCommentsSummaryError && !!errors.inlineCommentsSummaryError) ||
			(!!errors.footerCommentsSummaryError && inlineCommentsSummary.length === 0) ||
			(!!errors.inlineCommentsSummaryError && footerCommentsSummary.length === 0);

		return (
			<Stack xcss={styles} spread="space-between">
				<Box padding={isInObjectSidebar ? 'space.100' : 'space.250'}>
					{source !== CommentsSummarySource.Footer ? (
						<SummaryPopupScrollableBody data-testid="scrollable-comments-summary">
							{renderContentBody()}
						</SummaryPopupScrollableBody>
					) : (
						renderContentBody()
					)}
				</Box>
				<Box xcss={isInObjectSidebar && FooterObjectSidebarStyles}>
					<AISummaryFooter
						analyticsProperties={analyticsProperties}
						contentId={contentId}
						contentType={contentType}
						spaceKey={spaceKey}
						error={shouldShowFooterError}
						summaryText={fullTextToCopy()}
						showBetaLozenge
						shouldHighlightRefresh={shouldRefetchCommentsSummary}
						refreshHandler={refreshHandler}
						showAIDisclaimer={showAIDisclaimer}
					/>
				</Box>
			</Stack>
		);
	};

	return (
		<FlagsProvider>
			<FeedbackCollectorPropertiesProvider
				entrypointId={FOOTER_COMMENTS_SUMMARY_FEEDBACK_ENTRYPOINT_ID}
				feedbackContext={feedbackContext}
			>
				<div ref={containerRef}>
					<UFOLoadHold name="comments-summary-content" hold={loading} />
					{isInObjectSidebar ? (
						<>{renderContent()}</>
					) : (
						<AIBorder loading={loading}>{renderContent()}</AIBorder>
					)}
				</div>
			</FeedbackCollectorPropertiesProvider>
		</FlagsProvider>
	);
};
